import { __awaiter, __generator } from "tslib";
import { Texture as BJSTexture } from "@babylonjs/core/Materials/Textures/texture";
import { Matrix, Vector4 } from "@babylonjs/core/Maths/math.vector";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { BoxBuilder } from "@babylonjs/core/Meshes/Builders/boxBuilder";
import { NodeUserData } from "../core/mdl/NodeUserData";
import { PbrMaterial } from "../core/mdl/PbrMaterial";
import { getDataPath, Log } from "../utils";
import { PI_DIV2 } from "../utils/math";
import { isNullOrEmpty, joinPath } from "../utils/string";
import { ArticleElement } from "./main";
/**
 * Creates an empty TransformNode for the given article.
 */
var DummyPlaceholderCreator = /** @class */ (function () {
    function DummyPlaceholderCreator(pArticleManager) {
        this.mApp = pArticleManager.app;
    }
    DummyPlaceholderCreator.prototype.create = function (pArticle, pSubArticleIds) {
        return __awaiter(this, void 0, void 0, function () {
            var tNode, _i, pSubArticleIds_1, tSubId, tSubNode;
            return __generator(this, function (_a) {
                tNode = new TransformNode("article_dummyPlaceholder", this.mApp.scene);
                NodeUserData.SetData(tNode, ArticleElement.NODEDATA_BASKETID, pArticle.basketId);
                tNode.freezeWorldMatrix();
                //create sub articles
                for (_i = 0, pSubArticleIds_1 = pSubArticleIds; _i < pSubArticleIds_1.length; _i++) {
                    tSubId = pSubArticleIds_1[_i];
                    tSubNode = new TransformNode("subarticle_dummyPlaceholder", this.mApp.scene);
                    NodeUserData.SetData(tSubNode, ArticleElement.NODEDATA_BASKETID, tSubId);
                    tSubNode.parent = tNode;
                    tSubNode.freezeWorldMatrix();
                }
                return [2 /*return*/, (tNode)];
            });
        });
    };
    return DummyPlaceholderCreator;
}());
export { DummyPlaceholderCreator };
var DefaultPlaceholderCreator = /** @class */ (function () {
    function DefaultPlaceholderCreator(pArticleManager) {
        this.mApp = pArticleManager.app;
        this.mFrameImg = null;
        this.mDefaultCatalogImg = null;
    }
    DefaultPlaceholderCreator.prototype.create = function (pArticle, pSubArticleIds) {
        return __awaiter(this, void 0, void 0, function () {
            var tItemProps, tCatImage, tTexture, tMesh, _i, pSubArticleIds_2, tSubId, tSubNode;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, pArticle.getItemProperties()];
                    case 1:
                        tItemProps = _a.sent();
                        return [4 /*yield*/, pArticle.getCatalogImage()];
                    case 2:
                        tCatImage = _a.sent();
                        return [4 /*yield*/, this.createTexture(tItemProps, tCatImage)];
                    case 3:
                        tTexture = _a.sent();
                        return [4 /*yield*/, this.createMesh(tItemProps, tTexture)];
                    case 4:
                        tMesh = _a.sent();
                        //create sub articles
                        for (_i = 0, pSubArticleIds_2 = pSubArticleIds; _i < pSubArticleIds_2.length; _i++) {
                            tSubId = pSubArticleIds_2[_i];
                            tSubNode = new TransformNode("subarticle_placeholder", this.mApp.scene);
                            NodeUserData.SetData(tSubNode, ArticleElement.NODEDATA_BASKETID, tSubId);
                            tSubNode.parent = tMesh;
                            tSubNode.freezeWorldMatrix();
                        }
                        return [2 /*return*/, (tMesh)];
                }
            });
        });
    };
    DefaultPlaceholderCreator.prototype.createTexture = function (pItemProps, pCatalogImage) {
        return __awaiter(this, void 0, void 0, function () {
            var tFrameImg, tCatalogImg, tCanvas, tContext, tShorTextLines, tManSeriesLines, tRatio, tHeight, tY, tRatio, tWidth, tX, tLineHeight, tLineSpacing, tPosY, i, i;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.loadFrameImg()];
                    case 1:
                        tFrameImg = _a.sent();
                        tCatalogImg = null;
                        if (!(pCatalogImage != null)) return [3 /*break*/, 3];
                        return [4 /*yield*/, this.loadImgFromBuffer(pCatalogImage.data)];
                    case 2:
                        tCatalogImg = _a.sent();
                        _a.label = 3;
                    case 3:
                        if (!(tCatalogImg == null)) return [3 /*break*/, 5];
                        return [4 /*yield*/, this.loadDefaultCatalogImg()];
                    case 4:
                        tCatalogImg = _a.sent();
                        _a.label = 5;
                    case 5:
                        tCanvas = document.createElement("canvas");
                        tCanvas.width = 950;
                        tCanvas.height = 346;
                        tContext = tCanvas.getContext("2d");
                        if (tContext == null)
                            throw new Error(" Failed to get 2d canvas context");
                        //prepare text
                        tContext.font = "400 36px " + this.mApp.theme.fontFamily;
                        tShorTextLines = this.getTextLines(tContext, pItemProps.article.shortText, 545);
                        tContext.font = "400 24px " + this.mApp.theme.fontFamily;
                        tManSeriesLines = this.getTextLines(tContext, pItemProps.article.manufacturerName + " | " + pItemProps.article.seriesName, 545);
                        this.limitLines(tShorTextLines, tManSeriesLines.length > 1 ? 2 : 3);
                        this.limitLines(tManSeriesLines, 2);
                        //frame
                        tContext.drawImage(tFrameImg, 0, 0);
                        //catalog image
                        if (tCatalogImg != null) {
                            if (tCatalogImg.naturalWidth > tCatalogImg.naturalHeight) {
                                tRatio = tCatalogImg.naturalHeight / tCatalogImg.naturalWidth;
                                tHeight = Math.round(250 * tRatio);
                                tY = Math.round(48 + 125 - tHeight * 0.5);
                                tContext.drawImage(tCatalogImg, 48, tY, 250, tHeight);
                            }
                            else {
                                tRatio = tCatalogImg.naturalWidth / tCatalogImg.naturalHeight;
                                tWidth = Math.round(250 * tRatio);
                                tX = Math.round(48 + 125 - tWidth * 0.5);
                                tContext.drawImage(tCatalogImg, tX, 48, tWidth, 250);
                            }
                        }
                        tLineHeight = 36;
                        tLineSpacing = tLineHeight * 0.15;
                        tPosY = 94;
                        //article number
                        tContext.font = "700 36px " + this.mApp.theme.fontFamily;
                        tContext.fillStyle = "#4f4f4f";
                        tContext.fillText(pItemProps.article.baseArticleNumber, 335, tPosY);
                        //short text
                        tPosY += tLineHeight + 15;
                        tContext.font = "400 36px " + this.mApp.theme.fontFamily;
                        for (i = 0; i < tShorTextLines.length; ++i) {
                            if (i > 0)
                                tPosY += tLineHeight + tLineSpacing;
                            tContext.fillText(tShorTextLines[i], 335, tPosY);
                        }
                        //manufacturer and series
                        tPosY += tLineHeight + 15;
                        tLineHeight = 24;
                        tLineSpacing = tLineHeight * 0.15;
                        tContext.fillStyle = "#b2b2b2";
                        tContext.font = "400 24px " + this.mApp.theme.fontFamily;
                        for (i = 0; i < tManSeriesLines.length; ++i) {
                            if (i > 0)
                                tPosY += tLineHeight + tLineSpacing;
                            tContext.fillText(tManSeriesLines[i], 335, tPosY);
                        }
                        return [2 /*return*/, (tCanvas.toDataURL("image/jpg"))];
                }
            });
        });
    };
    DefaultPlaceholderCreator.prototype.createMesh = function (pItemProps, pTextureData) {
        var tName = "article_placeholder_" + pItemProps.article.baseArticleNumber;
        var tMaterialId = "article_placeholder_" + pItemProps.itemId;
        //dispose existing material and texture
        var tMat = this.mApp.scene.getMaterialByID(tMaterialId);
        if (tMat != null) {
            tMat.dispose(false, true);
        }
        //mesh
        var tFaceUVs = new Array();
        tFaceUVs.push(new Vector4(1, 1, 0, 0));
        tFaceUVs.push(new Vector4(0, 0, 1, 1));
        tFaceUVs.push(new Vector4(0, 0, 0, 0));
        tFaceUVs.push(new Vector4(0, 0, 0, 0));
        tFaceUVs.push(new Vector4(0, 0, 0, 0));
        tFaceUVs.push(new Vector4(0, 0, 0, 0));
        var tMesh = BoxBuilder.CreateBox(tName, {
            width: 1.0,
            height: 0.364,
            depth: 0.01,
            faceUV: tFaceUVs
        }, this.mApp.scene);
        tMesh.bakeTransformIntoVertices(Matrix.RotationX(PI_DIV2));
        tMesh.bakeTransformIntoVertices(Matrix.Translation(0.5, 0.005, -0.182));
        tMesh.freezeWorldMatrix();
        //material
        var tMaterial = new PbrMaterial(tName, this.mApp.model.materials);
        tMaterial.id = tMaterialId;
        tMaterial.backFaceCulling = false;
        tMaterial.metallic = 0;
        tMaterial.roughness = 1.0;
        tMaterial.albedoTexture = BJSTexture.LoadFromDataString(pItemProps.itemId, pTextureData, this.mApp.scene);
        tMaterial.albedoTexture.anisotropicFilteringLevel = this.mApp.engine.getCaps().maxAnisotropy;
        tMaterial.freeze();
        tMesh.material = tMaterial;
        //set basket id
        NodeUserData.SetData(tMesh, ArticleElement.NODEDATA_BASKETID, pItemProps.itemId);
        tMesh.freezeWorldMatrix();
        return (tMesh);
    };
    DefaultPlaceholderCreator.prototype.loadFrameImg = function () {
        var _this = this;
        if (this.mFrameImg != null) {
            return (Promise.resolve(this.mFrameImg));
        }
        else {
            return (this.loadImg(joinPath(getDataPath(), "cf/images/placeholder_frame.png")).then(function (pImg) {
                if (pImg != null) {
                    _this.mFrameImg = pImg;
                    return Promise.resolve(pImg);
                }
                else {
                    return Promise.reject("Failed to load frame imange.");
                }
            }));
        }
    };
    DefaultPlaceholderCreator.prototype.loadDefaultCatalogImg = function () {
        var _this = this;
        if (this.mDefaultCatalogImg != null) {
            return (Promise.resolve(this.mDefaultCatalogImg));
        }
        else {
            return (this.loadImg(joinPath(getDataPath(), "cf/images/placeholder_catalog_default.png")).then(function (pImg) {
                if (pImg != null) {
                    _this.mDefaultCatalogImg = pImg;
                    return Promise.resolve(pImg);
                }
                else {
                    return Promise.reject("Failed to load default catalog imange.");
                }
            }));
        }
    };
    DefaultPlaceholderCreator.prototype.loadImgFromBuffer = function (pBuffer) {
        if (pBuffer == null)
            return (Promise.resolve(null));
        var tBlob = new Blob([pBuffer]);
        var tBlobUrl = URL.createObjectURL(tBlob);
        var tPromise = this.loadImg(tBlobUrl).then(function (pImg) {
            URL.revokeObjectURL(tBlobUrl);
            return (pImg);
        });
        return (tPromise);
    };
    DefaultPlaceholderCreator.prototype.loadImg = function (pSrc) {
        if (isNullOrEmpty(pSrc))
            return (Promise.resolve(null));
        var tPromise = new Promise(function (pResolve) {
            var tImg = document.createElement("img");
            tImg.onload = function () { return pResolve(tImg); };
            tImg.onerror = function (pEvent) {
                Log.error("Failed to load image.", pEvent);
                pResolve(null);
            };
            tImg.src = pSrc;
        });
        return (tPromise);
    };
    DefaultPlaceholderCreator.prototype.getTextLines = function (pContext, pText, pMaxWidth) {
        if (pText == null)
            return ([""]);
        var words = pText.split(" ");
        var lines = [];
        var currentLine = words[0];
        for (var i = 1; i < words.length; i++) {
            var word = words[i];
            var width = pContext.measureText(currentLine + " " + word).width;
            if (width < pMaxWidth) {
                currentLine += " " + word;
            }
            else {
                lines.push(currentLine);
                currentLine = word;
            }
        }
        lines.push(currentLine);
        return lines;
    };
    DefaultPlaceholderCreator.prototype.limitLines = function (pLines, pMaxLines) {
        if (pLines.length > pMaxLines) {
            pLines.splice(pMaxLines);
            //replace last char of last line with ellipsis
            var tLastLine = pLines[pMaxLines - 1];
            tLastLine = tLastLine.substr(0, tLastLine.length - 1);
            tLastLine += "\u2026";
            pLines[pMaxLines - 1] = tLastLine;
        }
    };
    return DefaultPlaceholderCreator;
}());
export { DefaultPlaceholderCreator };
